/**
 * ItemReservationModal - Modal for creating new item reservations
 */

import React, { useState, useEffect, memo, useRef } from 'react';
import { XMarkIcon, MagnifyingGlassIcon, CheckCircleIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';

const ItemReservationModal = ({
  isOpen,
  onClose,
  onSuccess,
  inventoryItems,
  taxRate = 0,
  editMode = false,
  existingReservation = null
}) => {

  // Log tax rate for debugging
  useEffect(() => {
    if (isOpen) {
      console.log('ItemReservationModal - Tax Rate:', taxRate);
      console.log('Edit Mode:', editMode);
      console.log('Existing Reservation:', existingReservation);
    }
  }, [isOpen, taxRate, editMode, existingReservation]);

  // Fetch surcharge percentage when modal opens
  useEffect(() => {
    const fetchSurcharge = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/settings/financial/payment', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const percentage = response.data?.data?.surcharge?.percentage || 0;
        setSurchargePercentage(percentage);
        console.log('Fetched surcharge percentage:', percentage);
      } catch (error) {
        console.error('Error fetching surcharge:', error);
        setSurchargePercentage(0);
      }
    };

    if (isOpen) {
      fetchSurcharge();
    }
  }, [isOpen]);

  // Form data state
  const [formData, setFormData] = useState({
    // Customer Information
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    address: '',
    licenseNumber: '',
    insuranceInfo: '',
    secondCustomerFirstName: '',
    secondCustomerLastName: '',
    secondCustomerPhone: '',
    secondCustomerInsuranceProvider: '',
    secondCustomerPolicyNumber: '',

    // Reservation Details
    inventoryItemId: '',
    startTime: getCurrentLocalTime(),
    reservationDuration: '1',
    depositType: { cash: false, credit: false },
    depositAmount: '',
    paymentCashAmount: '',
    paymentCreditAmount: '',
    paymentId: '',
    notes: '',
    exceptions: [],
    numberOfPassengers: ''
  });

  // State for selected item details
  const [selectedItem, setSelectedItem] = useState(null);
  const [calculatedRates, setCalculatedRates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [surchargePercentage, setSurchargePercentage] = useState(0);

  // State for two-level dropdown filtering
  const [selectedVariantFilter, setSelectedVariantFilter] = useState('');

  // State for pre-fill lookup
  const [preFillSearchTerm, setPreFillSearchTerm] = useState('');
  const [preFillResults, setPreFillResults] = useState([]);
  const [preFillLoading, setPreFillLoading] = useState(false);
  const [preFillError, setPreFillError] = useState('');
  const [isPreFilled, setIsPreFilled] = useState(false);

  // State for start time buffer
  const [startTimeBufferMinutes, setStartTimeBufferMinutes] = useState(0);

  // State for preview and signature
  const [showPreview, setShowPreview] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const signaturePadRef = useRef(null);
  const firstDriverSignaturePadRef = useRef(null);
  const secondDriverSignaturePadRef = useRef(null);

  // Fetch start time buffer setting when modal opens
  useEffect(() => {
    const fetchStartTimeBuffer = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/settings/organization/general', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const buffer = response.data?.startTimeBufferMinutes || 0;
        setStartTimeBufferMinutes(buffer);

        // Update start time with buffer for new reservations
        if (!editMode) {
          setFormData(prev => ({
            ...prev,
            startTime: getCurrentLocalTime(buffer)
          }));
        }
      } catch (error) {
        console.error('Error fetching start time buffer:', error);
        setStartTimeBufferMinutes(0);
      }
    };

    if (isOpen && !editMode) {
      fetchStartTimeBuffer();
    }
  }, [isOpen, editMode]);

  // Get current local time with buffer in format for datetime-local input
  function getCurrentLocalTime(bufferMinutes = 0) {
    const now = new Date();
    // Add buffer minutes
    now.setMinutes(now.getMinutes() + bufferMinutes);
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  // Calculate end time based on start time and duration
  const calculateEndTime = () => {
    if (!formData.startTime || !formData.reservationDuration) return '';
    const start = new Date(formData.startTime);
    const duration = parseInt(formData.reservationDuration) || 0;
    const end = new Date(start.getTime() + duration * 60 * 60 * 1000);
    return end.toLocaleString();
  };

  // Update selected item when inventoryItemId changes
  useEffect(() => {
    if (formData.inventoryItemId) {
      let item = null;

      // In edit mode, check if the selected item is the reserved item
      if (editMode && existingReservation?.inventoryItem?.id === formData.inventoryItemId) {
        item = existingReservation.inventoryItem;
      } else if (inventoryItems) {
        // Otherwise look in available items
        item = inventoryItems.find(i => i.id === formData.inventoryItemId);
      }

      setSelectedItem(item);

      if (item) {
        // Calculate available rates for this item
        const rates = calculateAvailableRates(item);
        setCalculatedRates(rates);
      }
    } else {
      setSelectedItem(null);
      setCalculatedRates([]);
    }
  }, [formData.inventoryItemId, inventoryItems, editMode, existingReservation]);

  // Populate form with existing reservation data in edit mode
  useEffect(() => {
    if (editMode && existingReservation && isOpen) {
      console.log('📝 [ItemReservationModal] Populating form with existing reservation:', existingReservation);
      console.log('📝 [ItemReservationModal] License Number:', existingReservation.licenseNumber);
      console.log('📝 [ItemReservationModal] Insurance Info:', existingReservation.insuranceInfo);
      console.log('📝 [ItemReservationModal] Second Customer First Name:', existingReservation.secondCustomerFirstName);
      console.log('📝 [ItemReservationModal] Number of Passengers:', existingReservation.numberOfPassengers);
      console.log('📝 [ItemReservationModal] Notes:', existingReservation.notes);
      console.log('📝 [ItemReservationModal] Payment ID:', existingReservation.paymentId);

      // The inventory item data is in existingReservation.inventoryItem
      // (The item won't be in inventoryItems array since it's RESERVED)
      const existingItem = existingReservation.inventoryItem;

      if (existingItem) {
        // Build variant key using the same format as getUniqueVariants()
        const typeId = existingItem.productType?.id || 'unknown';
        const variantId = existingItem.productVariant?.id || 'no-variant';
        const variantKey = `${typeId}-${variantId}`;

        console.log('Setting variant filter to:', variantKey);
        console.log('Existing item:', existingItem);
        setSelectedVariantFilter(variantKey);
      }

      // Format start time for datetime-local input
      const formatDateForInput = (dateString) => {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      };

      // Determine deposit type from either depositType string or boolean fields
      const depositTypeCash = existingReservation.depositType === 'cash' || existingReservation.depositTypeCash || false;
      const depositTypeCredit = existingReservation.depositType === 'credit' || existingReservation.depositTypeCredit || false;

      // Get payment amounts from the payments array (first payment)
      const firstPayment = existingReservation.payments?.[0];
      const cashAmount = firstPayment?.cashAmount || 0;

      console.log('💰 Payment data from reservation:', {
        payments: existingReservation.payments,
        firstPayment,
        cashAmount
      });

      // Parse exceptions from existing reservation
      let exceptions = [];
      try {
        if (existingReservation.exceptions) {
          if (typeof existingReservation.exceptions === 'string') {
            exceptions = JSON.parse(existingReservation.exceptions);
          } else if (Array.isArray(existingReservation.exceptions)) {
            exceptions = existingReservation.exceptions;
          }
        }
      } catch (error) {
        console.error('Error parsing exceptions:', error);
        exceptions = [];
      }

      console.log('📋 Parsed exceptions:', exceptions);
      console.log('📋 Number of passengers:', existingReservation.numberOfPassengers);

      setFormData({
        // Customer Information
        firstName: existingReservation.customer?.firstName || '',
        lastName: existingReservation.customer?.lastName || '',
        phone: existingReservation.customer?.phone || '',
        email: existingReservation.customer?.email || '',
        address: existingReservation.customer?.address || '',
        licenseNumber: existingReservation.licenseNumber || '',
        insuranceInfo: existingReservation.insuranceInfo || '',
        secondCustomerFirstName: existingReservation.secondCustomerFirstName || '',
        secondCustomerLastName: existingReservation.secondCustomerLastName || '',
        secondCustomerPhone: existingReservation.secondCustomerPhone || '',
        secondCustomerInsuranceProvider: existingReservation.secondCustomerInsuranceProvider || '',
        secondCustomerPolicyNumber: existingReservation.secondCustomerPolicyNumber || '',

        // Reservation Details
        inventoryItemId: existingReservation.inventoryItemId || '',
        startTime: formatDateForInput(existingReservation.startTime),
        reservationDuration: String(existingReservation.reservationDuration || '1'),
        depositType: {
          cash: depositTypeCash,
          credit: depositTypeCredit
        },
        depositAmount: String(existingReservation.depositAmount || ''),
        paymentCashAmount: String(cashAmount),
        paymentCreditAmount: '',
        paymentId: existingReservation.paymentId || '',
        notes: existingReservation.notes || '',
        exceptions: exceptions,
        numberOfPassengers: String(existingReservation.numberOfPassengers || '')
      });
    }
  }, [editMode, existingReservation, isOpen]);

  // Reset preview state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setShowPreview(false);
      setSignatureData(null);
      setError('');
    }
  }, [isOpen]);

  // Calculate available rates for an item
  const calculateAvailableRates = (item) => {
    const availableRates = [];
    const rateTypes = ['hourly', 'daily', 'weekly', 'monthly', 'flat_rate'];
    
    rateTypes.forEach(rateType => {
      let rate = null;
      
      // Map rate type to uppercase for database comparison
      const dbRateType = rateType === 'flat_rate' ? 'FLAT_RATE' : rateType.toUpperCase();
      const legacyRateField = rateType === 'flat_rate' ? 'flatRateRate' : `${rateType}Rate`;
      
      // Check item-level rates first (highest priority)
      if (item[legacyRateField]) {
        rate = item[legacyRateField];
      }
      // Check product variant rates array
      else if (item.productVariant?.rates && Array.isArray(item.productVariant.rates)) {
        const foundRate = item.productVariant.rates.find(r => r.rateType === dbRateType);
        if (foundRate) {
          rate = foundRate.rate;
        }
      }
      // Check product variant legacy rates
      else if (item.productVariant?.[legacyRateField]) {
        rate = item.productVariant[legacyRateField];
      }
      // Check product type rates array
      else if (item.productType?.rates && Array.isArray(item.productType.rates)) {
        const foundRate = item.productType.rates.find(r => r.rateType === dbRateType);
        if (foundRate) {
          rate = foundRate.rate;
        }
      }
      // Check product type default rates (legacy) - check each rate type individually
      else if (rateType === 'hourly' && item.productType?.defaultHourlyRate) {
        rate = item.productType.defaultHourlyRate;
      }
      else if (rateType === 'daily' && item.productType?.defaultDailyRate) {
        rate = item.productType.defaultDailyRate;
      }
      else if (rateType === 'weekly' && item.productType?.defaultWeeklyRate) {
        rate = item.productType.defaultWeeklyRate;
      }
      else if (rateType === 'monthly' && item.productType?.defaultMonthlyRate) {
        rate = item.productType.defaultMonthlyRate;
      }
      else if (rateType === 'flat_rate' && item.productType?.defaultFlatRate) {
        rate = item.productType.defaultFlatRate;
      }
      
      if (rate && Number(rate) > 0) {
        const formatRateLabel = (type) => {
          switch(type) {
            case 'hourly': return { label: 'Hourly', unit: 'per hour' };
            case 'daily': return { label: 'Daily', unit: 'per day' };
            case 'weekly': return { label: 'Weekly', unit: 'per week' };
            case 'monthly': return { label: 'Monthly', unit: 'per month' };
            case 'flat_rate': return { label: 'Flat Rate', unit: 'one-time' };
            default: return { label: type, unit: `per ${type}` };
          }
        };
        
        const rateInfo = formatRateLabel(rateType);
        availableRates.push({
          type: rateType,
          rate: rate,
          label: rateInfo.label,
          unit: rateInfo.unit
        });
      }
    });
    
    return availableRates;
  };

  // Get unique product type/variant combinations for first dropdown
  const getUniqueVariants = () => {
    let itemsToProcess = inventoryItems || [];

    // In edit mode, include the currently reserved item
    if (editMode && existingReservation?.inventoryItem) {
      const reservedItem = existingReservation.inventoryItem;
      const isAlreadyInList = itemsToProcess.some(item => item.id === reservedItem.id);
      if (!isAlreadyInList) {
        itemsToProcess = [...itemsToProcess, reservedItem];
      }
    }

    if (!Array.isArray(itemsToProcess) || itemsToProcess.length === 0) return [];

    const variantMap = new Map();

    itemsToProcess.forEach(item => {
      const typeId = item.productType?.id || 'unknown';
      const typeName = item.productType?.name || 'Unknown Type';
      const variantId = item.productVariant?.id || 'no-variant';
      const variantName = item.productVariant?.name || null;

      // Create unique key combining type and variant
      const key = `${typeId}-${variantId}`;

      if (!variantMap.has(key)) {
        variantMap.set(key, {
          key,
          typeId,
          typeName,
          variantId,
          variantName,
          displayName: variantName ? `${typeName} - ${variantName}` : typeName
        });
      }
    });

    return Array.from(variantMap.values()).sort((a, b) =>
      a.displayName.localeCompare(b.displayName)
    );
  };

  // Get filtered items based on selected variant
  const getFilteredItems = () => {
    if (!inventoryItems || !Array.isArray(inventoryItems)) {
      // If no available items but we're in edit mode, return the reserved item
      if (editMode && existingReservation?.inventoryItem) {
        return [existingReservation.inventoryItem];
      }
      return [];
    }

    let items = inventoryItems;

    // In edit mode, include the currently reserved item
    if (editMode && existingReservation?.inventoryItem) {
      const reservedItem = existingReservation.inventoryItem;
      const isAlreadyInList = items.some(item => item.id === reservedItem.id);
      if (!isAlreadyInList) {
        items = [...items, reservedItem];
      }
    }

    if (!selectedVariantFilter) return items;

    return items.filter(item => {
      const typeId = item.productType?.id || 'unknown';
      const variantId = item.productVariant?.id || 'no-variant';
      const key = `${typeId}-${variantId}`;
      return key === selectedVariantFilter;
    });
  };

  // Handle variant filter change
  const handleVariantFilterChange = (e) => {
    const newFilter = e.target.value;
    setSelectedVariantFilter(newFilter);

    // Reset the selected item when filter changes
    setFormData(prev => ({
      ...prev,
      inventoryItemId: ''
    }));
  };

  // Calculate total cost with breakdown
  const calculateTotal = () => {
    if (!selectedItem || calculatedRates.length === 0) {
      return { subtotal: 0, tax: 0, total: 0 };
    }

    // For now, use hourly rate if available
    const hourlyRate = calculatedRates.find(r => r.type === 'hourly');
    if (hourlyRate) {
      const duration = parseFloat(formData.reservationDuration) || 0;
      const subtotal = hourlyRate.rate * duration;
      const tax = subtotal * (taxRate / 100);
      const total = subtotal + tax;
      return { subtotal, tax, total };
    }

    return { subtotal: 0, tax: 0, total: 0 };
  };

  // Calculate payment split with surcharge
  const calculatePaymentSplit = () => {
    const breakdown = calculateTotal();
    const totalAmount = breakdown.total;
    const cashAmount = parseFloat(formData.paymentCashAmount) || 0;
    const creditAmount = totalAmount - cashAmount;

    if (creditAmount <= 0) {
      return {
        cashAmount,
        creditAmount: 0,
        surchargeAmount: 0,
        creditChargeAmount: 0,
        totalToCollect: cashAmount
      };
    }

    // Calculate and round surcharge to 2 decimal places (standard for currency)
    const surchargeAmount = Math.round((creditAmount * surchargePercentage) * 100) / 100;
    const creditChargeAmount = Math.round((creditAmount + surchargeAmount) * 100) / 100;
    const totalToCollect = Math.round((cashAmount + creditChargeAmount) * 100) / 100;

    return {
      cashAmount,
      creditAmount,
      surchargeAmount,
      creditChargeAmount,
      totalToCollect
    };
  };

  // Handle pre-fill search
  const handlePreFillSearch = async (searchTerm) => {
    setPreFillSearchTerm(searchTerm);

    if (!searchTerm || searchTerm.trim().length < 2) {
      setPreFillResults([]);
      return;
    }

    setPreFillLoading(true);
    setPreFillError('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/customer-prefills/search?q=${encodeURIComponent(searchTerm)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setPreFillResults(response.data.data || []);
      }
    } catch (err) {
      console.error('Error searching pre-fills:', err);
      setPreFillError('Failed to search pre-fills');
      setPreFillResults([]);
    } finally {
      setPreFillLoading(false);
    }
  };

  // Handle pre-fill selection
  const handlePreFillSelect = async (shortId) => {
    setPreFillLoading(true);
    setPreFillError('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/customer-prefills/${shortId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        const preFillData = response.data.data;

        console.log('📋 Pre-fill data received:', preFillData);
        console.log('📋 Insurance provider:', preFillData.insuranceProvider);
        console.log('📋 Policy number:', preFillData.policyNumber);

        // Concatenate insurance provider and policy number
        const insuranceInfo = preFillData.policyNumber
          ? `${preFillData.insuranceProvider} - ${preFillData.policyNumber}`
          : preFillData.insuranceProvider || '';

        console.log('📋 Combined insurance info:', insuranceInfo);

        // Auto-populate form fields (including second customer if available)
        setFormData(prev => ({
          ...prev,
          firstName: preFillData.firstName,
          lastName: preFillData.lastName,
          phone: preFillData.phone,
          email: preFillData.email || '',
          address: preFillData.address || '',
          licenseNumber: preFillData.licenseNumber,
          insuranceInfo: insuranceInfo,
          secondCustomerFirstName: preFillData.secondCustomerFirstName || '',
          secondCustomerLastName: preFillData.secondCustomerLastName || '',
          secondCustomerPhone: preFillData.secondCustomerPhone || '',
          secondCustomerInsuranceProvider: preFillData.secondCustomerInsuranceProvider || '',
          secondCustomerPolicyNumber: preFillData.secondCustomerPolicyNumber || ''
        }));

        setIsPreFilled(true);
        setPreFillSearchTerm('');
        setPreFillResults([]);
      }
    } catch (err) {
      console.error('Error retrieving pre-fill:', err);
      setPreFillError(err.response?.data?.error || 'Failed to retrieve pre-fill');
    } finally {
      setPreFillLoading(false);
    }
  };

  // Handle form field changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // If user manually edits customer fields, remove pre-fill indicator
    if (['firstName', 'lastName', 'phone', 'email', 'address', 'licenseNumber', 'insuranceInfo'].includes(name)) {
      setIsPreFilled(false);
    }

    if (name === 'depositType') {
      const depositOption = e.target.getAttribute('data-option');
      setFormData(prev => ({
        ...prev,
        depositType: {
          ...prev.depositType,
          [depositOption]: checked
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  // Handle exceptions checkbox changes
  const handleExceptionChange = (exception) => {
    setFormData(prev => {
      const exceptions = prev.exceptions || [];
      if (exceptions.includes(exception)) {
        return {
          ...prev,
          exceptions: exceptions.filter(e => e !== exception)
        };
      } else {
        return {
          ...prev,
          exceptions: [...exceptions, exception]
        };
      }
    });
  };

  // Handle preview (validates form and shows preview)
  const handlePreview = async (e) => {
    e.preventDefault();
    setError('');

    try {
      // Validate required fields
      if (!formData.firstName || !formData.lastName || !formData.phone || !formData.licenseNumber || !formData.insuranceInfo) {
        throw new Error('Please fill in all required customer information');
      }

      if (!formData.inventoryItemId) {
        throw new Error('Please select an item');
      }

      if (!formData.depositType.cash && !formData.depositType.credit) {
        throw new Error('Please select a deposit type');
      }

      // Validate deposit amount
      if (!formData.depositAmount || formData.depositAmount.trim() === '') {
        throw new Error('Deposit amount is required');
      }
      const depositAmount = parseFloat(formData.depositAmount);
      if (isNaN(depositAmount) || depositAmount <= 0) {
        throw new Error('Please enter a valid deposit amount greater than $0');
      }

      // Validate payment amounts
      const breakdown = calculateTotal();
      const totalAmount = breakdown.total;
      const paymentCash = parseFloat(formData.paymentCashAmount) || 0;

      if (paymentCash < 0) {
        throw new Error('Cash amount cannot be negative');
      }

      if (paymentCash > totalAmount) {
        throw new Error(`Cash amount cannot exceed total ($${totalAmount.toFixed(2)})`);
      }

      // Validation passed, show preview
      setShowPreview(true);
    } catch (err) {
      setError(err.message);
    }
  };

  // Handle final confirmation with signature
  const handleConfirm = async () => {
    setError('');
    setLoading(true);

    try {
      // Get signature data
      if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) {
        throw new Error('Please provide a signature before confirming');
      }

      // Get first driver signature
      if (!firstDriverSignaturePadRef.current || firstDriverSignaturePadRef.current.isEmpty()) {
        throw new Error('Please provide the first driver signature');
      }

      // Get second driver signature if second driver info is provided
      if ((formData.secondCustomerFirstName || formData.secondCustomerLastName)) {
        if (!secondDriverSignaturePadRef.current || secondDriverSignaturePadRef.current.isEmpty()) {
          throw new Error('Please provide the second driver signature');
        }
      }

      const signatureDataUrl = signaturePadRef.current.toDataURL();
      const firstDriverSignatureDataUrl = firstDriverSignaturePadRef.current.toDataURL();
      const secondDriverSignatureDataUrl = (formData.secondCustomerFirstName || formData.secondCustomerLastName)
        ? secondDriverSignaturePadRef.current.toDataURL()
        : null;

      setSignatureData(signatureDataUrl);

      const breakdown = calculateTotal();
      const paymentSplit = calculatePaymentSplit();
      const baseAmount = breakdown.total; // Base amount before surcharge
      const paymentCash = parseFloat(formData.paymentCashAmount) || 0;

      const calculateEndTimeForSubmission = () => {
        if (!formData.startTime || !formData.reservationDuration) return null;
        const start = new Date(formData.startTime);
        const duration = parseFloat(formData.reservationDuration) || 0;
        const end = new Date(start.getTime() + duration * 60 * 60 * 1000);
        return end.toISOString();
      };

      // Prepare data for submission
      const reservationData = {
        customer: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          ...(formData.email && { email: formData.email }),
          ...(formData.address && { address: formData.address })
        },
        reservation: {
          inventoryItemId: formData.inventoryItemId,
          startTime: new Date(formData.startTime).toISOString(),
          endTime: calculateEndTimeForSubmission(),
          reservationDuration: parseFloat(formData.reservationDuration) || 0,
          depositType: formData.depositType, // Send object with cash/credit booleans
          depositAmount: parseFloat(formData.depositAmount) || 0,
          paymentType: paymentCash > 0 && paymentSplit.creditAmount > 0 ? 'split' : paymentCash > 0 ? 'cash' : 'credit',
          paymentAmount: baseAmount, // Send base amount, backend will calculate surcharge
          cashAmount: paymentCash, // Send as cashAmount for backend
          paymentId: formData.paymentId || null,
          notes: formData.notes || '',
          licenseNumber: formData.licenseNumber,
          insuranceInfo: formData.insuranceInfo,
          secondCustomerFirstName: formData.secondCustomerFirstName || null,
          secondCustomerLastName: formData.secondCustomerLastName || null,
          secondCustomerPhone: formData.secondCustomerPhone || null,
          secondCustomerInsuranceProvider: formData.secondCustomerInsuranceProvider || null,
          secondCustomerPolicyNumber: formData.secondCustomerPolicyNumber || null,
          exceptions: formData.exceptions && formData.exceptions.length > 0 ? formData.exceptions : null,
          numberOfPassengers: formData.numberOfPassengers ? parseInt(formData.numberOfPassengers, 10) : null,
          signature: signatureDataUrl, // Include signature
          firstDriverSignature: firstDriverSignatureDataUrl, // Include first driver signature
          secondDriverSignature: secondDriverSignatureDataUrl // Include second driver signature (null if not provided)
        }
      };

      // Submit to API
      let response;
      if (editMode && existingReservation) {
        // Update existing reservation
        response = await axios.put(`/api/reservations/items/${existingReservation.id}`, reservationData);
      } else {
        // Create new reservation
        response = await axios.post('/api/reservations/items/create', reservationData);
      }

      if (response.data) {
        // Success - call the onSuccess callback
        onSuccess();
      }
    } catch (err) {
      console.error(`Error ${editMode ? 'updating' : 'creating'} reservation:`, err);
      setError(err.response?.data?.message || err.message || `Failed to ${editMode ? 'update' : 'create'} reservation`);
    } finally {
      setLoading(false);
    }
  };

  // Handle clearing signature
  const handleClearSignature = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
    }
  };

  // Handle back to form from preview
  const handleBackToForm = () => {
    setShowPreview(false);
    setError('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
        
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
          <form onSubmit={handlePreview}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  {editMode ? 'Edit Item Reservation' : 'New Item Reservation'}
                </h3>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              {/* Form Content */}
              {!showPreview && (
                <>
                  {/* Pre-Fill Lookup */}
                  <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <MagnifyingGlassIcon className="h-5 w-5 text-blue-600 mr-2" />
                  <h4 className="text-md font-medium text-blue-900">Quick Fill from Pre-Filled Form</h4>
                </div>
                <p className="text-sm text-blue-700 mb-3">
                  Search for customers who pre-filled their information by name or ID
                </p>

                <div className="relative">
                  <input
                    type="text"
                    value={preFillSearchTerm}
                    onChange={(e) => handlePreFillSearch(e.target.value)}
                    placeholder="Search by name or ID (e.g., A1, John Smith)..."
                    className="w-full border border-blue-300 rounded-lg px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {preFillLoading && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    </div>
                  )}
                </div>

                {preFillError && (
                  <div className="mt-2 text-sm text-red-600">
                    {preFillError}
                  </div>
                )}

                {preFillResults.length > 0 && (
                  <div className="mt-3 max-h-48 overflow-y-auto border border-blue-200 rounded-lg bg-white">
                    {preFillResults.map((result) => (
                      <button
                        key={result.id}
                        type="button"
                        onClick={() => handlePreFillSelect(result.shortId)}
                        className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b border-blue-100 last:border-b-0 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-gray-900">
                              {result.firstName} {result.lastName}
                            </div>
                            <div className="text-sm text-gray-600">
                              {result.phone} • {result.email}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              License: {result.licenseNumber} • Insurance: {
                                result.policyNumber
                                  ? `${result.insuranceProvider} - ${result.policyNumber}`
                                  : result.insuranceProvider
                              }
                            </div>
                          </div>
                          <div className="ml-4 px-3 py-1 bg-blue-600 text-white text-xs font-bold rounded-md">
                            {result.shortId}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {isPreFilled && (
                  <div className="mt-3 flex items-center text-sm text-green-700">
                    <CheckCircleIcon className="h-5 w-5 mr-2" />
                    Customer information pre-filled
                  </div>
                )}
              </div>

              {/* Driver Information */}
              <div className="space-y-6">
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-4">Driver Information</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Address
                      </label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        License Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="licenseNumber"
                        value={formData.licenseNumber}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter driver's license number"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Insurance Information <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="insuranceInfo"
                        value={formData.insuranceInfo}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter insurance provider and policy number"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Second Driver Information (Optional) */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-2">Second Driver (Optional)</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    If there's a second driver on the reservation, please provide their information
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        First Name
                      </label>
                      <input
                        type="text"
                        name="secondCustomerFirstName"
                        value={formData.secondCustomerFirstName}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name
                      </label>
                      <input
                        type="text"
                        name="secondCustomerLastName"
                        value={formData.secondCustomerLastName}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone
                      </label>
                      <input
                        type="tel"
                        name="secondCustomerPhone"
                        value={formData.secondCustomerPhone}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Insurance Provider
                      </label>
                      <input
                        type="text"
                        name="secondCustomerInsuranceProvider"
                        value={formData.secondCustomerInsuranceProvider}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., State Farm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Policy Number
                      </label>
                      <input
                        type="text"
                        name="secondCustomerPolicyNumber"
                        value={formData.secondCustomerPolicyNumber}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="POL123456"
                      />
                    </div>
                  </div>
                </div>

                {/* Reservation Details */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-4">Reservation Details</h4>
                  <div className="space-y-4">
                    {/* Product Type/Variant Filter (First Dropdown) */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Product Type / Variant <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={selectedVariantFilter}
                        onChange={handleVariantFilterChange}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      >
                        <option value="">Choose a product type/variant...</option>
                        {getUniqueVariants().map(variant => (
                          <option key={variant.key} value={variant.key}>
                            {variant.displayName}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Specific Item Selection (Second Dropdown - shown only when variant selected) */}
                    {selectedVariantFilter && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Select Specific Item <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="inventoryItemId"
                          value={formData.inventoryItemId}
                          onChange={handleInputChange}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        >
                          <option value="">Choose an available item...</option>
                          {(() => {
                            const filteredItems = getFilteredItems();
                            return filteredItems.length > 0 ? (
                              filteredItems.map(item => {
                              // Helper function to get the best available rate for display
                              const getBestRateForDisplay = (item) => {

                                // Extended rate types based on your system
                                const rateTypes = [
                                  { field: 'hourlyRate', dbType: 'HOURLY', label: '/hr' },
                                  { field: 'dailyRate', dbType: 'DAILY', label: '/day' },
                                  { field: 'weeklyRate', dbType: 'WEEKLY', label: '/wk' },
                                  { field: 'monthlyRate', dbType: 'MONTHLY', label: '/mo' },
                                  { field: 'flatRate', dbType: 'FLAT_RATE', label: ' flat' },
                                  { field: 'perItemRate', dbType: 'PER_ITEM', label: '/item' },
                                  { field: 'perPersonRate', dbType: 'PER_PERSON', label: '/person' },
                                  { field: 'fixedRate', dbType: 'FIXED', label: ' fixed' }
                                ];
                                
                                // Try to find any rate in priority order
                                for (const rateType of rateTypes) {
                                  // Check item-level rates first (highest priority)
                                  if (item[rateType.field] && Number(item[rateType.field]) > 0) {
                                    return `$${Number(item[rateType.field]).toFixed(2)}${rateType.label}`;
                                  }
                                  
                                  // Check if productVariant exists and has rates (not null)
                                  if (item.productVariant && item.productVariant !== null) {
                                    // Check rates array on variant
                                    if (item.productVariant.rates && Array.isArray(item.productVariant.rates)) {
                                      const foundRate = item.productVariant.rates.find(r => {
                                        // Make comparison case-insensitive to handle both 'hourly' and 'HOURLY'
                                        const rateTypeMatch = r.rateType && r.rateType.toUpperCase() === rateType.dbType.toUpperCase();
                                        return rateTypeMatch && r.isActive !== false;
                                      });
                                      if (foundRate && foundRate.rate && Number(foundRate.rate) > 0) {
                                        return `$${Number(foundRate.rate).toFixed(2)}${rateType.label}`;
                                      }
                                    }
                                    // Check legacy rate fields on variant
                                    if (item.productVariant[rateType.field] && Number(item.productVariant[rateType.field]) > 0) {
                                      return `$${Number(item.productVariant[rateType.field]).toFixed(2)}${rateType.label}`;
                                    }
                                  }
                                  
                                  // Check if productType exists and has rates
                                  if (item.productType && item.productType !== null) {
                                    // Check rates array on product type - THIS IS WHERE YOUR DATA IS!
                                    if (item.productType.rates && Array.isArray(item.productType.rates)) {
                                      const foundRate = item.productType.rates.find(r => 
                                        r.rateType && r.rateType.toUpperCase() === rateType.dbType.toUpperCase() && r.isActive !== false
                                      );
                                      if (foundRate && foundRate.rate && Number(foundRate.rate) > 0) {
                                        return `$${Number(foundRate.rate).toFixed(2)}${rateType.label}`;
                                      }
                                    }
                                    // Check default rate fields on type (legacy)
                                    const defaultField = `default${rateType.field.charAt(0).toUpperCase()}${rateType.field.slice(1)}`;
                                    if (item.productType[defaultField] && Number(item.productType[defaultField]) > 0) {
                                      return `$${Number(item.productType[defaultField]).toFixed(2)}${rateType.label}`;
                                    }
                                  }
                                }
                                
                                // If no rates found, return null
                                return null;
                              };

                              const rateDisplay = getBestRateForDisplay(item);
                              
                              return (
                                <option key={item.id} value={item.id}>
                                  {item.productType?.name || 'Unknown'} #{item.itemNumber}
                                  {item.productVariant?.name ? ` - ${item.productVariant.name}` : ''}
                                  {rateDisplay ? ` - ${rateDisplay}` : ''}
                                </option>
                              );
                              })
                            ) : (
                              <option value="" disabled>No items available for this variant</option>
                            );
                          })()}
                        </select>
                      </div>
                    )}

                    {/* Show rates for selected item */}
                    {selectedItem && calculatedRates.length > 0 && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="text-sm font-medium text-gray-700 mb-2">Available Rates</div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                          {calculatedRates.map((rateInfo, index) => (
                            <div key={index} className="bg-white border border-gray-200 rounded-lg p-3">
                              <div className="text-sm font-medium text-gray-700">{rateInfo.label}</div>
                              <div className="text-lg font-bold text-green-600">
                                ${Number(rateInfo.rate).toFixed(2)}
                              </div>
                              <div className="text-xs text-gray-500">{rateInfo.unit}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Start Time <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="datetime-local"
                          name="startTime"
                          value={formData.startTime}
                          onChange={handleInputChange}
                          className="w-full max-w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Duration (hours) <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          name="reservationDuration"
                          value={formData.reservationDuration}
                          onChange={handleInputChange}
                          min="1"
                          step="0.5"
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          # of Passengers
                        </label>
                        <input
                          type="number"
                          name="numberOfPassengers"
                          value={formData.numberOfPassengers}
                          onChange={handleInputChange}
                          min="0"
                          step="1"
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    {/* Exceptions Section */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Exceptions
                      </label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {['Military', 'Car Seat', 'Senior', 'Wheelchair', 'Group', 'Other'].map(exception => (
                          <label key={exception} className="flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={(formData.exceptions || []).includes(exception)}
                              onChange={() => handleExceptionChange(exception)}
                              className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">{exception}</span>
                          </label>
                        ))}
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        Select applicable exceptions for special accommodations
                      </p>
                    </div>

                    {formData.startTime && formData.reservationDuration && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                        <div className="font-medium text-blue-900">Expected Return</div>
                        <div className="text-blue-700">{calculateEndTime()}</div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Deposit Type <span className="text-red-500">*</span>
                        </label>
                        <div className="space-y-2">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              name="depositType"
                              data-option="cash"
                              checked={formData.depositType.cash}
                              onChange={handleInputChange}
                              className="mr-2"
                            />
                            <span className="text-sm">Cash</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              name="depositType"
                              data-option="credit"
                              checked={formData.depositType.credit}
                              onChange={handleInputChange}
                              className="mr-2"
                            />
                            <span className="text-sm">Credit Card</span>
                          </label>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Deposit Amount <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          name="depositAmount"
                          value={formData.depositAmount}
                          required
                          onChange={handleInputChange}
                          onWheel={(e) => e.target.blur()}
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Payment ID (Optional)
                      </label>
                      <input
                        type="text"
                        name="paymentId"
                        value={formData.paymentId}
                        onChange={handleInputChange}
                        placeholder="Enter payment ID for tracking..."
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Notes (Optional)
                      </label>
                      <textarea
                        name="notes"
                        value={formData.notes}
                        onChange={handleInputChange}
                        rows="3"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Any special instructions or notes..."
                      />
                    </div>

                    {/* Total Calculation */}
                    {selectedItem && calculatedRates.length > 0 && (() => {
                      const breakdown = calculateTotal();
                      return (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <div className="space-y-2">
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-gray-700">Subtotal</span>
                              <span className="font-medium text-gray-900">
                                ${breakdown.subtotal.toFixed(2)}
                              </span>
                            </div>
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-gray-700">Tax ({taxRate}%)</span>
                              <span className="font-medium text-gray-900">
                                ${breakdown.tax.toFixed(2)}
                              </span>
                            </div>
                            <div className="border-t border-green-300 pt-2 flex justify-between items-center">
                              <span className="text-sm font-medium text-gray-700">Estimated Total</span>
                              <span className="text-lg font-bold text-green-700">
                                ${breakdown.total.toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Payment Amount */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Payment Amount <span className="text-red-500">*</span>
                      </label>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Cash
                          </label>
                          <input
                            type="number"
                            name="paymentCashAmount"
                            value={formData.paymentCashAmount}
                            onChange={handleInputChange}
                            onWheel={(e) => e.target.blur()}
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          />
                        </div>

                        {/* Credit Card Split Breakdown */}
                        {(() => {
                          const paymentSplit = calculatePaymentSplit();
                          if (paymentSplit.creditAmount > 0) {
                            return (
                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <div className="space-y-2">
                                  <div className="flex justify-between items-center text-sm">
                                    <span className="text-gray-700">Credit Amount</span>
                                    <span className="font-medium text-gray-900">
                                      ${paymentSplit.creditAmount.toFixed(2)}
                                    </span>
                                  </div>
                                  {paymentSplit.surchargeAmount > 0 && (
                                    <div className="flex justify-between items-center text-sm">
                                      <span className="text-gray-700">Surcharge ({(surchargePercentage * 100).toFixed(2)}%)</span>
                                      <span className="font-medium text-gray-900">
                                        ${paymentSplit.surchargeAmount.toFixed(2)}
                                      </span>
                                    </div>
                                  )}
                                  <div className="border-t border-blue-300 pt-2 flex justify-between items-center">
                                    <span className="text-sm font-medium text-gray-700">Charge on Card</span>
                                    <span className="text-lg font-bold text-blue-700">
                                      ${paymentSplit.creditChargeAmount.toFixed(2)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })()}

                        <div className="bg-gray-50 border border-gray-300 rounded-lg p-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-gray-700">Total to Collect</span>
                            <span className="text-lg font-bold text-gray-900">
                              ${calculatePaymentSplit().totalToCollect.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <p className="mt-2 text-xs text-gray-500">
                        Enter the cash amount. Credit amount is auto-calculated. Surcharge applies to credit card payments.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading
                    ? (editMode ? 'Updating...' : 'Creating...')
                    : (editMode ? 'Update Reservation' : 'Preview')
                  }
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
                </>
              )}

          {/* Preview Page with Signature */}
          {showPreview && (
            <>
              <div className="space-y-6">
                {/* Back Button */}
                <button
                  type="button"
                  onClick={handleBackToForm}
                  className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
                >
                  <ArrowLeftIcon className="h-5 w-5 mr-2" />
                  Back to Form
                </button>

                <h4 className="text-lg font-semibold text-gray-900 border-b pb-2">Reservation Preview</h4>

                {/* Driver Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h5 className="font-medium text-gray-900 mb-3">Driver Information</h5>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-600">Name:</span>
                      <p className="text-gray-900">{formData.firstName} {formData.lastName}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Phone:</span>
                      <p className="text-gray-900">{formData.phone}</p>
                    </div>
                    {formData.email && (
                      <div>
                        <span className="font-medium text-gray-600">Email:</span>
                        <p className="text-gray-900">{formData.email}</p>
                      </div>
                    )}
                    {formData.address && (
                      <div>
                        <span className="font-medium text-gray-600">Address:</span>
                        <p className="text-gray-900">{formData.address}</p>
                      </div>
                    )}
                    <div>
                      <span className="font-medium text-gray-600">License Number:</span>
                      <p className="text-gray-900">{formData.licenseNumber}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Insurance Info:</span>
                      <p className="text-gray-900">{formData.insuranceInfo}</p>
                    </div>
                  </div>

                  {/* First Driver Alcohol Statement and Signature */}
                  <div className="mt-4 pt-4 border-t border-gray-300">
                    <p className="text-sm text-gray-700 mb-3 font-medium">
                      I have not consumed any alcohol in the last 6 hours
                    </p>
                    <div className="bg-white border-2 border-gray-300 rounded-lg">
                      <SignatureCanvas
                        ref={firstDriverSignaturePadRef}
                        canvasProps={{
                          className: 'w-full h-32 rounded-lg',
                          style: { touchAction: 'none' }
                        }}
                      />
                    </div>
                    <div className="mt-2 flex justify-between items-center">
                      <span className="text-xs text-gray-500">First Driver Signature</span>
                      <button
                        type="button"
                        onClick={() => firstDriverSignaturePadRef.current?.clear()}
                        className="text-sm text-gray-600 hover:text-gray-800 underline"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                </div>

                {/* Second Driver (if applicable) */}
                {(formData.secondCustomerFirstName || formData.secondCustomerLastName) && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h5 className="font-medium text-gray-900 mb-3">Second Driver</h5>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-gray-600">Name:</span>
                        <p className="text-gray-900">{formData.secondCustomerFirstName} {formData.secondCustomerLastName}</p>
                      </div>
                      {formData.secondCustomerPhone && (
                        <div>
                          <span className="font-medium text-gray-600">Phone:</span>
                          <p className="text-gray-900">{formData.secondCustomerPhone}</p>
                        </div>
                      )}
                      {formData.secondCustomerInsuranceProvider && (
                        <div>
                          <span className="font-medium text-gray-600">Insurance Provider:</span>
                          <p className="text-gray-900">{formData.secondCustomerInsuranceProvider}</p>
                        </div>
                      )}
                      {formData.secondCustomerPolicyNumber && (
                        <div>
                          <span className="font-medium text-gray-600">Policy Number:</span>
                          <p className="text-gray-900">{formData.secondCustomerPolicyNumber}</p>
                        </div>
                      )}
                    </div>

                    {/* Second Driver Alcohol Statement and Signature */}
                    <div className="mt-4 pt-4 border-t border-gray-300">
                      <p className="text-sm text-gray-700 mb-3 font-medium">
                        I have not consumed any alcohol in the last 6 hours
                      </p>
                      <div className="bg-white border-2 border-gray-300 rounded-lg">
                        <SignatureCanvas
                          ref={secondDriverSignaturePadRef}
                          canvasProps={{
                            className: 'w-full h-32 rounded-lg',
                            style: { touchAction: 'none' }
                          }}
                        />
                      </div>
                      <div className="mt-2 flex justify-between items-center">
                        <span className="text-xs text-gray-500">Second Driver Signature</span>
                        <button
                          type="button"
                          onClick={() => secondDriverSignaturePadRef.current?.clear()}
                          className="text-sm text-gray-600 hover:text-gray-800 underline"
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Reservation Details */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h5 className="font-medium text-gray-900 mb-3">Reservation Details</h5>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-600">Item:</span>
                      <p className="text-gray-900">
                        {selectedItem
                          ? `${selectedItem.productType?.name || 'Unknown'} #${selectedItem.itemNumber}`
                          : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Start Time:</span>
                      <p className="text-gray-900">{new Date(formData.startTime).toLocaleString()}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Duration:</span>
                      <p className="text-gray-900">{formData.reservationDuration} hour(s)</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">End Time:</span>
                      <p className="text-gray-900">{calculateEndTime()}</p>
                    </div>
                    {formData.numberOfPassengers && (
                      <div>
                        <span className="font-medium text-gray-600">Number of Passengers:</span>
                        <p className="text-gray-900">{formData.numberOfPassengers}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Payment Details */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h5 className="font-medium text-gray-900 mb-3">Payment Details</h5>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-600">Deposit Type:</span>
                      <p className="text-gray-900">
                        {formData.depositType.cash ? 'Cash' : 'Credit'}
                      </p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Deposit Amount:</span>
                      <p className="text-gray-900">${parseFloat(formData.depositAmount || 0).toFixed(2)}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Cash Payment:</span>
                      <p className="text-gray-900">${parseFloat(formData.paymentCashAmount || 0).toFixed(2)}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Total:</span>
                      <p className="text-gray-900 font-semibold">${calculateTotal().total.toFixed(2)}</p>
                    </div>
                  </div>
                </div>

                {/* Notes (if any) */}
                {formData.notes && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h5 className="font-medium text-gray-900 mb-2">Notes</h5>
                    <p className="text-sm text-gray-900">{formData.notes}</p>
                  </div>
                )}

                {/* Signature Section */}
                <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4">
                  <h5 className="font-medium text-blue-900 mb-3">Customer Signature</h5>
                  <p className="text-sm text-blue-700 mb-3">
                    Please sign below to confirm the reservation details above:
                  </p>
                  <div className="bg-white border-2 border-gray-300 rounded-lg">
                    <SignatureCanvas
                      ref={signaturePadRef}
                      canvasProps={{
                        className: 'w-full h-48 rounded-lg',
                        style: { touchAction: 'none' }
                      }}
                    />
                  </div>
                  <div className="mt-3 flex justify-end">
                    <button
                      type="button"
                      onClick={handleClearSignature}
                      className="text-sm text-gray-600 hover:text-gray-800 underline"
                    >
                      Clear Signature
                    </button>
                  </div>
                </div>
              </div>

              {/* Preview Page Buttons */}
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse mt-6">
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={loading}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Confirming...' : 'Confirm Reservation'}
                </button>
                <button
                  type="button"
                  onClick={handleBackToForm}
                  disabled={loading}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Back to Form
                </button>
              </div>
            </>
          )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default memo(ItemReservationModal);